'use client';

import { autoStartDemoAtom } from '@/features/demo-flow/atoms/demoFlowAtoms';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { Button } from '@heidi/design-system/button';
import { FileSparkle, Message, Microphone } from '@heidi/design-system/icons';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useTranslations } from '@heidi/runtime/i18n';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';

import { track } from '@/lib/analytics/track';
import {
  type ScribeOnboardingFeature,
  ScribeOnboardingModalLayout,
} from './ScribeOnboardingModalLayout';
import { ScribeOnboardingModalSvg } from './scribe-onboarding-modal-svg';

export const ScribeOnboardingModal = () => {
  const { t } = useTranslations();
  const { isOpen, handleClose, dismissible } = useQueuedModal(ModalId.SCRIBE_ONBOARDING);
  const setAutoStartDemo = useSetAtom(autoStartDemoAtom);
  const features = useMemo<ScribeOnboardingFeature[]>(
    () => [
      {
        icon: Microphone,
        text: t('scribeOnboardingModal.features.transcribeSessions'),
      },
      {
        icon: FileSparkle,
        text: t('scribeOnboardingModal.features.createNotes'),
      },
      {
        icon: Message,
        text: t('scribeOnboardingModal.features.clinicalAnswers'),
      },
    ],
    [t]
  );

  useEffect(() => {
    if (isOpen) {
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.ModalViewed,
        props: { screen_name: ScreenName.ScribeOnboardingModal },
      });
    }
  }, [isOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  return (
    <ScribeOnboardingModalLayout
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      dismissible={dismissible}
      title={t('scribeOnboardingModal.title')}
      subtitle={t('scribeOnboardingModal.subtitle')}
      features={features}
      image={
        // Match the old export's intrinsic 452x538 render; the inline SVG
        // carries its own translated aria-label.
        <ScribeOnboardingModalSvg width={452} height={538} className="shrink-0" />
      }
    >
      <Button
        variant="default"
        size="lg"
        className="w-full"
        onClick={() => {
          setAutoStartDemo(true);
          handleClose();
        }}
      >
        {t('scribeOnboardingModal.walkthroughDemo')}
      </Button>
      <div className="flex justify-center">
        <Button
          size="lg"
          onClick={handleClose}
          variant="link"
          className="!text-background-primary no-underline hover:!text-background-primary-hover hover:underline"
        >
          {t('scribeOnboardingModal.exploreScribe')}
        </Button>
      </div>
    </ScribeOnboardingModalLayout>
  );
};
